import { useState, useEffect, useCallback, useRef } from 'react';

// Types pour Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

// Déclaration globale pour le navigateur
declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

// Détecter si on est sur mobile
const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  ) || (navigator.maxTouchPoints > 0 && /Macintosh/.test(navigator.userAgent));
};

// Détecter iOS Safari (ne supporte pas bien le mode continu)
const isIOSSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.maxTouchPoints > 0 && /Macintosh/.test(ua)); // iPad avec iOS 13+
  const isWebKit = /WebKit/.test(ua);
  const isChrome = /CriOS/.test(ua);
  const isFirefox = /FxiOS/.test(ua);
  return isIOS && isWebKit && !isChrome && !isFirefox;
};

// Vérifier si l'API Speech Recognition est disponible
const getSpeechRecognitionAPI = (): SpeechRecognitionConstructor | null => {
  if (typeof window === 'undefined') return null;

  // Essayer les différentes implémentations
  // Safari utilise webkitSpeechRecognition
  const SpeechRecognitionAPI =
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition ||
    (window as any).mozSpeechRecognition ||
    (window as any).msSpeechRecognition;

  return SpeechRecognitionAPI || null;
};

// Vérifier si on est sur HTTPS (requis pour le microphone sur mobile)
const isSecureContext = (): boolean => {
  if (typeof window === 'undefined') return false;
  // localhost est considéré comme sécurisé même en HTTP
  return window.isSecureContext ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
};

// Vérifier si on est probablement sur un navigateur qui supporte l'API
// (même si on ne peut pas le vérifier immédiatement)
const isProbablySupported = (): boolean => {
  if (typeof window === 'undefined') return false;

  const ua = navigator.userAgent;

  // Chrome (desktop et Android)
  if (/Chrome/.test(ua) && !/Edge/.test(ua)) return true;

  // Safari 14.5+ (desktop et iOS)
  if (/Safari/.test(ua) && /Version\/(\d+)\.(\d+)/.test(ua)) {
    const match = ua.match(/Version\/(\d+)\.(\d+)/);
    if (match && match[1] && match[2]) {
      const major = parseInt(match[1], 10);
      const minor = parseInt(match[2], 10);
      // Safari 14.5+ supporte Web Speech API
      if (major > 14 || (major === 14 && minor >= 5)) return true;
    }
  }

  // Edge
  if (/Edg/.test(ua)) return true;

  // Firefox (support limité)
  if (/Firefox/.test(ua)) return false;

  return false;
};

export interface UseSpeechRecognitionReturn {
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  isSupported: boolean;
  isMobile: boolean;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
  requestPermission: () => Promise<boolean>;
}

interface UseSpeechRecognitionOptions {
  language?: string;
  continuous?: boolean;
  interimResults?: boolean;
  onResult?: (transcript: string) => void;
  onEnd?: () => void;
}

/**
 * Hook pour la reconnaissance vocale via Web Speech API
 * Optimisé pour mobile (iOS Safari, Android Chrome)
 */
export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {}
): UseSpeechRecognitionReturn {
  const {
    language = 'fr-FR',
    continuous: requestedContinuous = true,
    interimResults = true,
    onResult,
    onEnd,
  } = options;

  // iOS Safari ne supporte pas bien le mode continu
  const continuous = isIOSSafari() ? false : requestedContinuous;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Par défaut, on suppose que c'est supporté sur les navigateurs modernes
  const [isSupported, setIsSupported] = useState(() => isProbablySupported());
  const [isMobileDevice] = useState(isMobile());

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const restartTimeoutRef = useRef<number | null>(null);
  const shouldRestartRef = useRef(false);
  const interimTranscriptRef = useRef('');
  const transcriptRef = useRef('');

  // Vérifier le support du navigateur
  useEffect(() => {
    const SpeechRecognitionAPI = getSpeechRecognitionAPI();

    // Si l'API est disponible, l'utiliser
    if (SpeechRecognitionAPI) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognitionAPI();

      const recognition = recognitionRef.current;
      recognition.continuous = continuous;
      recognition.interimResults = interimResults;
      recognition.lang = language;

      recognition.onstart = () => {
        setIsListening(true);
        setError(null);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interim = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (!result || !result[0]) continue;
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interim += result[0].transcript;
          }
        }

        if (finalTranscript) {
          setTranscript((prev) => {
            const newTranscript = prev ? `${prev} ${finalTranscript}` : finalTranscript;
            transcriptRef.current = newTranscript;
            onResult?.(newTranscript);
            return newTranscript;
          });
        }

        interimTranscriptRef.current = interim;
        setInterimTranscript(interim);
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        let errorMessage = 'Une erreur est survenue';

        switch (event.error) {
          case 'not-allowed':
          case 'permission-denied':
            errorMessage = isMobileDevice
              ? 'Autorisez le microphone dans Réglages > Safari > Microphone'
              : 'Veuillez autoriser l\'accès au microphone';
            break;
          case 'no-speech':
            // Sur mobile, on peut redémarrer automatiquement si pas de parole détectée
            if (isMobileDevice && shouldRestartRef.current) {
              restartTimeoutRef.current = window.setTimeout(() => {
                if (recognitionRef.current && shouldRestartRef.current) {
                  try {
                    recognitionRef.current.start();
                  } catch (e) {
                    console.warn('Impossible de redémarrer:', e);
                  }
                }
              }, 100);
              return;
            }
            errorMessage = 'Aucune parole détectée. Réessayez.';
            break;
          case 'network':
            errorMessage = 'Erreur réseau - vérifiez votre connexion internet';
            break;
          case 'audio-capture':
            errorMessage = 'Aucun microphone détecté';
            break;
          case 'aborted':
            // Ignoré - arrêt volontaire
            return;
          case 'service-not-allowed':
            errorMessage = isMobileDevice
              ? 'Service non disponible. Utilisez Chrome ou Safari.'
              : 'Service de reconnaissance vocale non disponible';
            break;
          default:
            errorMessage = `Erreur: ${event.error}`;
        }

        setError(errorMessage);
        setIsListening(false);
        shouldRestartRef.current = false;
      };

      recognition.onend = () => {
        // Sur iOS Safari (non-continu), redémarrer automatiquement si on veut continuer
        if (isIOSSafari() && shouldRestartRef.current) {
          restartTimeoutRef.current = window.setTimeout(() => {
            if (recognitionRef.current && shouldRestartRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                console.warn('Impossible de redémarrer sur iOS:', e);
                setIsListening(false);
                shouldRestartRef.current = false;
                onEnd?.();
              }
            }
          }, 100);
          return;
        }

        setIsListening(false);
        setInterimTranscript('');
        shouldRestartRef.current = false;
        onEnd?.();
      };
    } else {
      // L'API n'est pas disponible directement
      // Mais on garde isSupported à true si on pense que ça devrait marcher
      // (l'erreur sera gérée quand l'utilisateur essaiera)
      if (!isProbablySupported()) {
        setIsSupported(false);
        setError(
          isMobile()
            ? 'Reconnaissance vocale non supportée. Utilisez Chrome ou Safari 14.5+.'
            : 'Votre navigateur ne supporte pas la reconnaissance vocale'
        );
      }
    }

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      shouldRestartRef.current = false;
    };
  }, [language, continuous, interimResults, onResult, onEnd, isMobileDevice]);

  // Demander la permission du microphone (utile sur mobile)
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Arrêter le stream immédiatement, on veut juste la permission
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err) {
      console.error('Permission microphone refusée:', err);
      setError(
        isMobileDevice
          ? 'Autorisez le microphone dans les paramètres'
          : 'Permission microphone refusée'
      );
      return false;
    }
  }, [isMobileDevice]);

  const startListening = useCallback(() => {
    // Vérifier si on est sur HTTPS (requis sur mobile)
    if (isMobileDevice && !isSecureContext()) {
      setError('La saisie vocale nécessite HTTPS. Utilisez https:// dans l\'URL.');
      return;
    }

    // Si l'API n'est pas encore initialisée, essayer de l'initialiser maintenant
    if (!recognitionRef.current) {
      const SpeechRecognitionAPI = getSpeechRecognitionAPI();

      if (SpeechRecognitionAPI) {
        recognitionRef.current = new SpeechRecognitionAPI();
        const recognition = recognitionRef.current;
        recognition.continuous = continuous;
        recognition.interimResults = interimResults;
        recognition.lang = language;

        recognition.onstart = () => {
          setIsListening(true);
          setError(null);
        };

        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let finalTranscript = '';
          let interim = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (!result || !result[0]) continue;
            if (result.isFinal) {
              finalTranscript += result[0].transcript;
            } else {
              interim += result[0].transcript;
            }
          }
          if (finalTranscript) {
            setTranscript((prev) => {
              const newTranscript = prev ? `${prev} ${finalTranscript}` : finalTranscript;
              transcriptRef.current = newTranscript;
              return newTranscript;
            });
          }
          interimTranscriptRef.current = interim;
          setInterimTranscript(interim);
        };

        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
          if (event.error !== 'aborted') {
            let errorMsg = `Erreur: ${event.error}`;
            if (event.error === 'not-allowed' || event.error === 'permission-denied') {
              errorMsg = isMobileDevice
                ? 'Microphone non autorisé. Allez dans Réglages > Safari.'
                : 'Veuillez autoriser l\'accès au microphone';
            }
            setError(errorMsg);
            setIsListening(false);
          }
        };

        recognition.onend = () => {
          setIsListening(false);
          setInterimTranscript('');
        };
      } else {
        setError(
          isMobileDevice
            ? 'Reconnaissance vocale non disponible sur ce navigateur'
            : 'Votre navigateur ne supporte pas la reconnaissance vocale'
        );
        setIsSupported(false);
        return;
      }
    }

    if (recognitionRef.current && !isListening) {
      setError(null);
      setTranscript('');
      setInterimTranscript('');
      transcriptRef.current = '';
      interimTranscriptRef.current = '';
      shouldRestartRef.current = true;

      try {
        recognitionRef.current.start();
      } catch (err: any) {
        shouldRestartRef.current = false;

        // Afficher une erreur plus explicite
        if (err.name === 'NotAllowedError' || err.message?.includes('not allowed')) {
          setError(isMobileDevice
            ? 'Microphone non autorisé. Allez dans Réglages > Safari pour autoriser.'
            : 'Veuillez autoriser l\'accès au microphone');
        } else {
          setError(`Erreur: ${err.message || err}`);
        }
      }
    }
  }, [isListening, continuous, interimResults, language, isMobileDevice]);

  const stopListening = useCallback(() => {
    shouldRestartRef.current = false;
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }

    // Capturer les résultats temporaires avant d'arrêter
    // car sur iOS Safari ils pourraient ne pas devenir "final"
    const currentInterim = interimTranscriptRef.current;
    if (currentInterim && currentInterim.trim()) {
      setTranscript((prev) => {
        const combined = prev
          ? `${prev} ${currentInterim.trim()}`
          : currentInterim.trim();
        transcriptRef.current = combined;
        return combined;
      });
      interimTranscriptRef.current = '';
      setInterimTranscript('');
    }

    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  }, [isListening]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    transcriptRef.current = '';
    interimTranscriptRef.current = '';
    setError(null);
  }, []);

  return {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    isMobile: isMobileDevice,
    startListening,
    stopListening,
    resetTranscript,
    requestPermission,
  };
}
